import React, { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { SearchInput } from "../../../pages/psychologist/tabs/patients/components/SearchInput";
import Avatar from "../../common/Avatar";
import CircularProgress from "../../common/CircularProgress";
import { useToast } from "../../../hooks/useToast";
import { useTrackSearchUsed } from "../../../hooks/useTrackSearchUsed";
import { calendarPath } from "../../../routes/slugs";
import { usePatientSearchIndex, canShowPatientEmail, type SearchablePatient } from "./hooks/usePatientSearchIndex";

const MAX_RESULTS = 25;

// Unicode combining diacritical marks (U+0300–U+036F): lo que separa "García" de "Garcia"
// tras un normalize("NFD"). Se recorre por code point en vez de usar un literal regex para
// evitar problemas de escaping de "\u" en algunas cadenas de herramientas.
const isCombiningMark = (codePoint: number): boolean => codePoint >= 0x0300 && codePoint <= 0x036f;

const normalize = (s: string): string =>
  Array.from((s || "").normalize("NFD"))
    .filter((ch) => !isCombiningMark(ch.codePointAt(0) || 0))
    .join("")
    .toLowerCase()
    .trim();

const formatShortDate = (iso?: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat("es-ES", { day: "2-digit", month: "short", year: "numeric" })
    .format(d)
    .replace(".", "");
};

// Mismo parseo que usePatientsData.onGoToCalendar: las fechas de sesión vienen como
// ISO completo o "YYYY-MM-DD HH:mm:ss"; nos quedamos solo con la parte de fecha.
const extractDateOnly = (raw: string): string => raw.split("T")[0].split(" ")[0];

/**
 * Ícono de búsqueda de pacientes para el header de la Agenda (semanal, mensual y diaria).
 * Al escribir, filtra por nombre o mail (sin distinguir acentos) contra el índice de
 * `usePatientSearchIndex` y muestra última/próxima sesión de cada resultado. Al elegir uno,
 * navega a la vista Diaria en la fecha de su próxima sesión (o la última, si no tiene
 * próxima agendada) — funciona igual sin importar desde qué vista de la Agenda se buscó.
 */
export function PatientAgendaSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const { patients, loading, ensureLoaded } = usePatientSearchIndex();
  const { showInfo } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inputRef = useRef<HTMLInputElement>(null);

  const results = useMemo(() => {
    const q = normalize(query);
    if (!q) return [];
    return patients
      .filter((p) => normalize(p.name).includes(q) || normalize(p.email).includes(q))
      .slice(0, MAX_RESULTS);
  }, [patients, query]);

  // Métricas de Actividad: cuenta el USO del buscador (nunca lo escrito).
  useTrackSearchUsed("search_patients_agenda", query, results.length);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      if (next) {
        ensureLoaded();
        setQuery("");
        setHighlightedIndex(-1);
      }
    },
    [ensureLoaded]
  );

  // Precarga al pasar el mouse / enfocar el ícono (bastante antes del click real) para que,
  // al abrir el diálogo, los pacientes ya estén (o casi) listos y no se perciba como demora
  // del click en sí — el fetch de por_page:800 puede tardar más que la apertura del diálogo.
  const handlePrefetch = useCallback(() => ensureLoaded(), [ensureLoaded]);

  const handleSelect = useCallback(
    (patient: SearchablePatient) => {
      setOpen(false);
      const targetIso = patient.nextSessionDate || patient.lastSessionDate;
      if (!targetIso) {
        showInfo(`${patient.name || "Este paciente"} todavía no tiene sesiones agendadas.`);
        return;
      }
      const next = new URLSearchParams(searchParams);
      next.set("date", extractDateOnly(targetIso));
      navigate(`${calendarPath("day")}?${next.toString()}`);
    },
    [navigate, searchParams, showInfo]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const idx = highlightedIndex >= 0 ? highlightedIndex : 0;
      const p = results[idx];
      if (p) handleSelect(p);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid="patient-agenda-search-trigger"
        aria-label="Buscar paciente en la agenda"
        title="Buscar paciente"
        onClick={() => handleOpenChange(true)}
        onMouseEnter={handlePrefetch}
        onFocus={handlePrefetch}
        className="grid place-items-center p-1.5 rounded-full border-0 bg-primary/10 text-primary shadow-sm hover:bg-primary/15 hover:shadow-md transition-all cursor-pointer flex-shrink-0"
      >
        <Search size={16} strokeWidth={2.5} />
      </button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent data-testid="patient-agenda-search-dialog" className="sm:max-w-md flex flex-col p-0 overflow-hidden max-h-[85vh] sm:max-h-[32rem]">
          <div className="px-4 pt-1 pb-3 sm:pt-4 border-b border-border flex-shrink-0">
            <DialogTitle className="text-base">Buscar paciente</DialogTitle>
            <DialogDescription className="sr-only">
              Buscá un paciente por nombre o mail para ir directo al día de su próxima o última sesión.
            </DialogDescription>
            <div className="mt-3">
              <SearchInput
                ref={inputRef}
                placeholder="Nombre o mail del paciente"
                value={query}
                onChange={(v) => {
                  setQuery(v);
                  setHighlightedIndex(-1);
                }}
                onKeyDown={handleKeyDown}
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto min-h-[3rem]">
            {query.trim().length === 0 ? (
              // Prioridad sobre "loading": recién abierto, sin nada escrito, no tiene sentido
              // mostrar un spinner de carga — el fetch sigue en curso en segundo plano (con
              // prefetch en hover ya suele estar resuelto para cuando el usuario termina de
              // escribir la primera letra) y no bloquea nada visible todavía.
              <p className="px-4 py-6 text-sm text-muted-foreground text-center">
                Escribí un nombre o mail para buscar.
              </p>
            ) : loading && patients.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <CircularProgress sizeRem={2} label="Cargando pacientes..." />
              </div>
            ) : results.length === 0 ? (
              <p className="px-4 py-6 text-sm text-muted-foreground text-center">
                No encontramos pacientes para &ldquo;{query}&rdquo;.
              </p>
            ) : (
              <ul role="listbox" aria-label="Resultados de pacientes" className="py-1">
                {results.map((p, idx) => {
                  const last = formatShortDate(p.lastSessionDate);
                  const next = formatShortDate(p.nextSessionDate);
                  const showEmail = canShowPatientEmail(p.patientStatus);
                  // Si no hay nombre, el email es el único identificador visible que queda —
                  // pero solo cuando el criterio de status permite mostrarlo (mismo caso límite
                  // que "no aporta valor de seguimiento" de PatientTable).
                  const displayName = p.name || (showEmail ? p.email : "Paciente");
                  return (
                    <li key={p.processId}>
                      <button
                        type="button"
                        role="option"
                        data-testid={`patient-search-result-${p.processId}`}
                        aria-selected={highlightedIndex === idx}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleSelect(p);
                        }}
                        onMouseEnter={() => setHighlightedIndex(idx)}
                        className={cn(
                          "flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors",
                          highlightedIndex === idx ? "bg-primary/10" : "bg-transparent hover:bg-muted"
                        )}
                      >
                        <Avatar name={displayName} sizeRem={2.25} className="flex-shrink-0" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-semibold text-sm text-foreground">
                            {displayName}
                          </span>
                          {p.name && p.email && showEmail && (
                            <span className="block truncate text-xs text-muted-foreground">{p.email}</span>
                          )}
                        </span>
                        <span className="flex-shrink-0 text-right text-xs leading-tight">
                          {next ? (
                            <span className="block font-medium text-primary">Próxima: {next}</span>
                          ) : (
                            <span className="block text-muted-foreground/70">Sin próxima sesión</span>
                          )}
                          {last && <span className="block text-muted-foreground">Última: {last}</span>}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default PatientAgendaSearch;
